/// <summary>
/// Placeholder container for React Prop

import { IPagePlaceholder } from '../services/models/PageViewModel';
import * as Layout from './LayoutObject';
import { Rnd, Position, ResizableDelta } from 'react-rnd';
import { ResizeDirection } from 're-resizable';

export type ResizeEvent = {
  direction: ResizeDirection;
  delta: ResizableDelta;
  position: Position;
  // When present, adjacent containers can align the shared edge to the exact snapped boundary.
  snappedBoundaryX?: number;
  snappedBoundaryY?: number;
};

/// <summary>
export class TPagePlaceholder {
  public readonly serverData: IPagePlaceholder;
  private readonly newServerData: IPagePlaceholder;
  public rndRef: Rnd | null = null;
  public setControlledState: ((next: { x: number; y: number; width: number; height: number }) => void) | null = null;
  private readonly layoutElement: Layout.TLayoutElement;
  private rect: Layout.TRect;

  constructor(aServerData: IPagePlaceholder) {
    this.serverData = aServerData;
    this.newServerData = { ...aServerData };
    this.rect = new Layout.TRect(aServerData.left, aServerData.top, aServerData.width, aServerData.height);
    this.layoutElement = Layout.createElement(this.rect, this);
  }

  public resize(aEvent: ResizeEvent, aResizeDirection: Layout.IResizeEdgeObject | null) {
    if (aResizeDirection === null) {
      let resizeDirection = Layout.ResizeEdge.None;
      switch (aEvent.direction) {
        case 'left':
          resizeDirection = Layout.ResizeEdge.Left;
          break;
        case 'right':
          resizeDirection = Layout.ResizeEdge.Right;
          break;
        case 'top':
          resizeDirection = Layout.ResizeEdge.Top;
          break;
        case 'bottom':
          resizeDirection = Layout.ResizeEdge.Bottom;
          break;
        default:
          resizeDirection = Layout.ResizeEdge.None;
      }
      //this.serverData.left = aEvent.position.x;
      //this.serverData.top = aEvent.position.y;
      this.layoutElement.resize(aEvent, resizeDirection);
    } else {
      const applyVisualUpdate = (nextLeft: number, nextTop: number, nextWidth: number, nextHeight: number) => {
        if (this.setControlledState) {
          this.setControlledState({ x: nextLeft, y: nextTop, width: nextWidth, height: nextHeight });
          return;
        }
        if (this.rndRef) {
          this.rndRef.updateSize({ width: nextWidth, height: nextHeight });
          this.rndRef.updatePosition({ x: nextLeft, y: nextTop });
        }
      };

      let direction = 1;
      let delta = aEvent.delta.width;

      switch (aResizeDirection) {
        case Layout.ResizeEdge.Right: {
          if (aEvent.direction !== 'right') {
            direction = -1;
          }
          delta = aEvent.delta.width * direction;

          const nextWidth = typeof aEvent.snappedBoundaryX === 'number' ? aEvent.snappedBoundaryX - this.serverData.left : this.serverData.width + delta;

          this.newServerData.width = nextWidth;
          applyVisualUpdate(this.serverData.left, this.serverData.top, nextWidth, this.serverData.height);
          break;
        }
        case Layout.ResizeEdge.Left: {
          if (aEvent.direction !== 'left') {
            direction = -1;
          }
          delta = aEvent.delta.width * direction;

          let nextLeft = this.serverData.left - delta;
          let nextWidth = this.serverData.width + delta;

          if (typeof aEvent.snappedBoundaryX === 'number') {
            const originalRight = this.serverData.left + this.serverData.width;
            nextLeft = aEvent.snappedBoundaryX;
            nextWidth = originalRight - nextLeft;
          }

          this.newServerData.width = nextWidth;
          this.newServerData.left = nextLeft;
          applyVisualUpdate(nextLeft, this.serverData.top, nextWidth, this.serverData.height);
          break;
        }
        case Layout.ResizeEdge.Bottom: {
          if (aEvent.direction !== 'bottom') {
            direction = -1;
          }
          delta = aEvent.delta.height * direction;

          const nextHeight = typeof aEvent.snappedBoundaryY === 'number' ? aEvent.snappedBoundaryY - this.serverData.top : this.serverData.height + delta;

          this.newServerData.height = nextHeight;
          applyVisualUpdate(this.serverData.left, this.serverData.top, this.serverData.width, nextHeight);
          break;
        }
        case Layout.ResizeEdge.Top: {
          if (aEvent.direction !== 'top') {
            direction = -1;
          }
          delta = aEvent.delta.height * direction;

          let nextTop = this.serverData.top - delta;
          let nextHeight = this.serverData.height + delta;

          if (typeof aEvent.snappedBoundaryY === 'number') {
            const originalBottom = this.serverData.top + this.serverData.height;
            nextTop = aEvent.snappedBoundaryY;
            nextHeight = originalBottom - nextTop;
          }

          this.newServerData.height = nextHeight;
          this.newServerData.top = nextTop;
          applyVisualUpdate(this.serverData.left, nextTop, this.serverData.width, nextHeight);
          break;
        }
      }
    }
  }

  public ResizePlaceholder(useOriginData: boolean): void {
    if (!useOriginData) {
      this.serverData.left = this.newServerData.left;
      this.serverData.top = this.newServerData.top;
      this.serverData.width = this.newServerData.width;
      this.serverData.height = this.newServerData.height;
      this.serverData.hasChanged = true;
    } else {
      this.newServerData.left = this.serverData.left;
      this.newServerData.top = this.serverData.top;
      this.newServerData.width = this.serverData.width;
      this.newServerData.height = this.serverData.height;
      this.newServerData.hasChanged = this.serverData.hasChanged;
    }
    this.rect.update(this.serverData.left, this.serverData.top, this.serverData.width, this.serverData.height);
  }
} // End of class TPlaceHolder
